/**
 * DateTimeStep
 * Step 2: Select Date & Time
 */

import { FiCalendar } from "react-icons/fi";
import Button from "@/components/ui/Button";
import InlineDatePicker from "@/components/booking/InlineDatePicker";
import { BookingFormData, BookingSettings } from "@/types";
import { TimeSlotOption, BookingFormErrors } from "../types";

interface DateTimeStepProps {
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  settings: BookingSettings | null;
  availableTimeSlots: TimeSlotOption[];
  checkingAvailability: boolean;
  onFormChange: (updates: Partial<BookingFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

export default function DateTimeStep({
  formData,
  errors,
  settings,
  availableTimeSlots,
  checkingAvailability,
  onFormChange,
  onNext,
  onBack,
}: DateTimeStepProps) {
  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
          <FiCalendar className="text-primary-600" />
          Select Date & Time
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400 mt-1 text-sm">
          Choose your preferred date and time slot
        </p>
      </div>

      {/* Date & Time Selection */}
      <div className="bg-white dark:bg-neutral-800 p-6 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Calendar */}
          <div>
            <InlineDatePicker
              selectedDate={formData.scheduledDate ?? undefined}
              minDate={new Date()}
              maxDate={
                new Date(Date.now() + (settings?.bookingWindowDays || 30) * 24 * 60 * 60 * 1000)
              }
              isDayEnabled={(date) => {
                if (!settings) return true;
                const dayName = [
                  "sunday",
                  "monday",
                  "tuesday",
                  "wednesday",
                  "thursday",
                  "friday",
                  "saturday",
                ][date.getDay()] as keyof typeof settings.weeklySchedule;
                return settings.weeklySchedule[dayName].enabled;
              }}
              onSelect={(date) => {
                const normalized = new Date(date);
                normalized.setHours(0, 0, 0, 0);
                onFormChange({
                  scheduledDate: normalized,
                  timeSlotId: undefined,
                });
              }}
            />
          </div>

          {/* Time Slots */}
          <div className="flex flex-col">
            <label className="block text-sm font-semibold text-neutral-900 dark:text-white mb-4">
              🕐 Available Time Slots *
            </label>

            {!formData.scheduledDate ? (
              <div className="flex items-center justify-center flex-1 text-neutral-500 dark:text-neutral-400 text-sm">
                Select a date to view available times
              </div>
            ) : checkingAvailability ? (
              <div className="flex items-center justify-center flex-1 text-neutral-600 dark:text-neutral-300">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
                Checking availability...
              </div>
            ) : availableTimeSlots.length === 0 ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
                <p className="text-amber-800 dark:text-amber-200 font-medium mb-2">
                  No available time slots
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  Please select a different date
                </p>
              </div>
            ) : (
              <>
                <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-4">
                  {new Date(formData.scheduledDate).toLocaleDateString("en-US", {
                    weekday: "long",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
                <div
                  className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-1"
                  role="radiogroup"
                  aria-label="Select time slot"
                >
                  {availableTimeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      role="radio"
                      aria-checked={formData.timeSlotId === slot.id}
                      onClick={() =>
                        onFormChange({
                          timeSlotId: slot.id,
                          scheduledTime: slot.time,
                        })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onFormChange({
                            timeSlotId: slot.id,
                            scheduledTime: slot.time,
                          });
                        }
                      }}
                      tabIndex={0}
                      className={`p-3 rounded-lg font-medium transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm ${
                        formData.timeSlotId === slot.id
                          ? "bg-primary-600 text-white shadow-lg border-2 border-primary-600"
                          : "bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-700 hover:border-primary-400 hover:shadow-md text-neutral-700 dark:text-neutral-200"
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              </>
            )}
            {errors.timeSlotId && <p className="text-red-600 text-sm mt-3">{errors.timeSlotId}</p>}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onNext} disabled={!formData.timeSlotId}>
          Next
        </Button>
      </div>
    </div>
  );
}
